<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { storeToRefs } from "pinia";

import { useModalStore } from "@/stores/modals";

import { useInspectMaterialTools } from "@/features/InspectMaterialToolsNew";
import { useTooltip } from "@/stores/useTooltip";
import { useLibrary } from "@/stores/project/library.store";
import { useNewConstruction } from "@/stores/Views/CreateConstruction/NewConstructionStore";

import Modal from "@/components/Reusables/Modal/Modal.vue";
import Button from "@/components/Reusables/Buttons/Button.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import { Copy, ExternalLink, SquarePen, Trash2 } from "@lucide/vue";
import { ConstructionFormRoute } from "@/router/constructionRoutes";

const props = withDefaults(
  defineProps<{
    materialName: string;
    material: object;
    materialId: string;
    tenantId: string;
    allowEdit?: boolean;
    allowClone?: boolean;
    allowDelete?: boolean;
    isConstruction?: boolean;
    showOpenInNewTab?: boolean;
  }>(),
  {
    allowEdit: false,
    allowClone: false,
    allowDelete: false,
    isConstruction: false,
    showOpenInNewTab: true,
  }
);

const inspectMaterialTools = useInspectMaterialTools();
const tooltip = useTooltip();
const router = useRouter();
const modals = useModalStore();

const libraryStore = useLibrary();
const { fetchLibraryMaterials, setLibrary } = libraryStore;

const newConstruction = useNewConstruction();
const { constructionIsDirty } = storeToRefs(newConstruction);

const clicked = ref(false);

const iconClass = computed(() => [
  "cursor-pointer rounded-4 p-px-6 transition-all",
  {
    "pointer-events-none opacity-40": inspectMaterialTools.getLoading(),
    "hover:bg-slate-400 hover:text-slate-200": !inspectMaterialTools.getLoading(),
  },
]);

function setClicked() {
  clicked.value = true;
}

const openInNewTab = () => {
  const route = router.resolve({
    name: "libraryDetail",
    params: { id: props.materialId },
  });
  setLibrary(props.material);
  window.open(route.href, "_blank");
};

function handleOpenInNewTab() {
  setClicked();
  openInNewTab();
}

const handleEdit = async () => {
  if (constructionIsDirty.value) {
    const res = await modals.open(
      "DiscardModal",
      {},
      {
        wrapperClass: "max-w-md min-w-0 min-h-[0px]",
      }
    );
    if (res.action == "confirm") {
      inspectMaterialTools.editMaterial(props.materialId);
      setClicked();
    }
  } else {
    inspectMaterialTools.editMaterial(props.materialId);
    setClicked();
  }
};

const handleClone = async () => {
  if (constructionIsDirty.value) {
    const res = await modals.open(
      "DiscardModal",
      {},
      {
        wrapperClass: "max-w-md min-w-0 min-h-[0px]",
      }
    );
    if (res.action == "confirm") {
      if (props.isConstruction) {
        inspectMaterialTools.cloneConstruction(props.materialId);
      } else {
        inspectMaterialTools.cloneMaterial(props.materialId);
      }
    }
  } else {
    if (props.isConstruction) {
      inspectMaterialTools.cloneConstruction(props.materialId);
    } else {
      inspectMaterialTools.cloneMaterial(props.materialId);
    }
  }
};

function handleDelete() {
  setClicked();
  inspectMaterialTools.toggleDeleteModal(props.materialId);
}

async function handleConfirmDelete() {
  await inspectMaterialTools.deleteMaterialOrConstruction(props.materialId, props.isConstruction);
  await fetchLibraryMaterials(true);
}

watch(
  () => inspectMaterialTools.getLoading(),
  (value) => {
    if (!value) {
      clicked.value = false;
    }
  }
);
</script>

<template>
  <div class="space-x-px-4 text-type-default t-small flex">
    <ExternalLink
      v-if="showOpenInNewTab"
      class="icon-sm"
      :class="iconClass"
      :stroke-width="1.5"
      @click="handleOpenInNewTab"
      @mouseenter="tooltip.showTooltip('Open in new tab')"
      @mouseleave="tooltip.hideTooltip()" />
    <template v-if="inspectMaterialTools.allowTenantToEdit(tenantId)">
      <div
        v-if="clicked && inspectMaterialTools.getLoading()"
        class="flex items-center">
        <LoadingSpinner
          size="sm"
          fill="primary" />
      </div>

      <template v-if="allowEdit">
        <RouterLink
          v-if="isConstruction"
          :to="{ name: ConstructionFormRoute.Edit, params: { id: materialId } }">
          <SquarePen
            class="icon-sm"
            :class="iconClass"
            :stroke-width="1.5"
            @click="setClicked"
            @mouseenter="tooltip.showTooltip('Edit')"
            @mouseleave="tooltip.hideTooltip()" />
        </RouterLink>
        <SquarePen
          v-else
          class="icon-sm"
          :class="iconClass"
          :stroke-width="1.5"
          @click="handleEdit"
          @mouseenter="tooltip.showTooltip('Edit')"
          @mouseleave="tooltip.hideTooltip()" />
      </template>

      <Copy
        v-if="allowClone"
        class="icon-sm"
        :class="iconClass"
        :stroke-width="1.5"
        @click="handleClone"
        @mouseenter="tooltip.showTooltip('Clone')"
        @mouseleave="tooltip.hideTooltip()" />

      <Trash2
        v-if="allowDelete"
        class="icon-sm"
        :class="iconClass"
        :stroke-width="1.5"
        @click="handleDelete"
        @mouseenter="tooltip.showTooltip('Delete')"
        @mouseleave="tooltip.hideTooltip()" />
    </template>
  </div>
  <Modal
    :show="() => inspectMaterialTools.showDeleteModal(materialId)"
    size="w-fit h-fit"
    heading="Are you sure?"
    :toggle-modal="() => inspectMaterialTools.toggleDeleteModal()">
    <div class="space-y-px-24 flex flex-col">
      <span>You are about to delete (deprecate) "{{ materialName }}".</span>
      <div class="flex justify-between">
        <Button
          label="Delete"
          color="destructive"
          :on-click="handleConfirmDelete" />
        <Button
          label="Cancel"
          color="button"
          :on-click="() => inspectMaterialTools.toggleDeleteModal()" />
      </div>
    </div>
  </Modal>
</template>
